/**
 * HQ's API refuses a socket connect ON PURPOSE in two cases, and says so: the caller is over its
 * connect budget (`rate-limited`, with `retryAfterSeconds`), or too many HQ pages are connected
 * from one network address (`too-many-connections`). See
 * packages/api/src/channelSystem/security/connectBudget.ts. socket.io hands the sentence and
 * `data` to the client's `connect_error`, where nothing read them: the person saw "Server
 * Disconnected", and then calls that failed after 100 s as "Timeout of 100000ms exceeded calling
 * create on 'data-mapper-data-source'" (item #3981, the 2026-10-22 Data Mapper onboarding).
 *
 * This shows the server's sentence once per refusal, remembers it while it lasts, words a call
 * that timed out meanwhile as what happened, and CONNECTS AGAIN: socket.io-client destroys a socket
 * whose handshake a server middleware refused ("clean subscriptions to avoid reconnections"), so
 * the page stayed offline until a reload, and each reload spent the budget again (Codex r1). It
 * never says the call was not sent: socket.io keeps what was emitted while disconnected and may
 * still send it once the page reconnects.
 */

/** The refusal reasons HQ's socket layer sends (connectBudget.ts, socketSecurity.ts). */
export const SERVER_REFUSALS: ReadonlySet<string> = new Set(["rate-limited", "too-many-connections"]);

export interface ConnectRefusal {
  message: string;
  reason: string;
  retryAfterSeconds: number | null;
}

interface SocketLike {
  on(event: string, listener: (...args: unknown[]) => void): unknown;
  off(event: string, listener: (...args: unknown[]) => void): unknown;
  connect?: () => unknown;
  connected?: boolean;
}

export interface WatchOptions {
  /** False while the page must stay disconnected (signed out, torn down): nothing reconnects then. */
  mayReconnect?: () => boolean;
  /** Timers (tests replace them). */
  setTimer?: (fn: () => void, ms: number) => unknown;
  clearTimer?: (timer: unknown) => void;
}

/** The longest wait between tries while too many pages are connected. */
export const MAX_RECONNECT_WAIT_MS = 5 * 60_000;

/**
 * When to connect again after a refusal: just past the moment HQ said it accepts the next one, or,
 * while too many pages are connected (nothing says when one closes), 30 s doubling to 5 minutes.
 */
export function reconnectDelayMs(refusal: ConnectRefusal, attempt: number): number {
  if (refusal.reason === "rate-limited") return (refusal.retryAfterSeconds ?? 15) * 1000 + 250;
  return Math.min(30_000 * 2 ** Math.max(0, attempt), MAX_RECONNECT_WAIT_MS);
}

let current: ConnectRefusal | null = null;

/** The refusal this page's connection is under now; null once it has connected again. */
export const currentConnectRefusal = (): ConnectRefusal | null => current;

/** A connect_error HQ refused on purpose, or null for any other failure (the network, a timeout). */
export function refusalOf(err: unknown): ConnectRefusal | null {
  const e = err as { message?: unknown; data?: { reason?: unknown; retryAfterSeconds?: unknown } } | null;
  const reason = e?.data?.reason;
  if (typeof reason !== "string" || !SERVER_REFUSALS.has(reason)) return null;
  if (typeof e?.message !== "string" || e.message === "") return null;
  const wait = e.data?.retryAfterSeconds;
  return {
    message: e.message,
    reason,
    retryAfterSeconds: typeof wait === "number" && Number.isFinite(wait) ? wait : null,
  };
}

/**
 * Watch `socket` for HQ's refusals: tell the person once per refusal (every try while refused
 * raises the same error), remember it until the next connect, and connect again when HQ will take
 * it (reconnectDelayMs). Returns the unsubscribe.
 */
export function watchConnectRefusals(
  socket: SocketLike,
  notify: (message: string) => void,
  options: WatchOptions = {}
): () => void {
  const setTimer = options.setTimer ?? ((fn: () => void, ms: number) => setTimeout(fn, ms));
  const clearTimer = options.clearTimer ?? ((t: unknown) => clearTimeout(t as ReturnType<typeof setTimeout>));
  const mayReconnect = options.mayReconnect ?? (() => true);
  let announced = false;
  let attempts = 0;
  let timer: unknown = null;
  const cancel = () => {
    if (timer !== null) clearTimer(timer);
    timer = null;
  };
  const onError = (err: unknown) => {
    const refusal = refusalOf(err);
    if (!refusal) return;
    current = refusal;
    if (!announced) {
      announced = true;
      notify(refusal.message);
    }
    cancel();
    const wait = reconnectDelayMs(refusal, attempts);
    attempts += 1;
    timer = setTimer(() => {
      timer = null;
      if (socket.connected || !mayReconnect()) return;
      socket.connect?.();
    }, wait);
  };
  const onConnect = () => {
    announced = false;
    attempts = 0;
    current = null;
    cancel();
  };
  socket.on("connect_error", onError);
  socket.on("connect", onConnect);
  return () => {
    socket.off("connect_error", onError);
    socket.off("connect", onConnect);
    cancel();
    current = null;
  };
}

/** A Feathers client timeout while HQ refuses this page's connection, in words; else null. */
export function refusedTimeoutMessage(err: unknown): string | null {
  const refusal = current;
  if (!refusal) return null;
  const e = err as { name?: unknown; message?: unknown } | null;
  if (e?.name !== "Timeout" || typeof e.message !== "string" || !e.message.startsWith("Timeout of ")) return null;
  return `HQ did not answer, because this page could not connect to it. ${refusal.message}`;
}

interface HookableApp {
  hooks: (hooks: { error: { all: Array<(context: { error?: unknown }) => void> } }) => unknown;
}

/** Word every call that times out while HQ refuses this page's connection (refusedTimeoutMessage). */
export function installRefusedTimeoutLabels(app: unknown): void {
  const hookable = app as HookableApp | null;
  if (typeof hookable?.hooks !== "function") return;
  hookable.hooks({
    error: {
      all: [
        (context) => {
          const message = refusedTimeoutMessage(context.error);
          if (message && context.error && typeof context.error === "object") {
            (context.error as { message: string }).message = message;
          }
        },
      ],
    },
  });
}
